import { useState, useEffect, useCallback } from "react";
import { useTenantNavigation } from "@/hooks/useTenantNavigation";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { format, startOfMonth } from "date-fns";
import {
  LogOut, BarChart3, FileText, ChevronRight, ChevronLeft,
  Download, Eye, Calendar, MapPin, Truck, Clock, FileSpreadsheet,
  Trash2, RotateCcw
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export default function ViewerPage() {
  const { user, logout } = useAuth();
  const { navigate } = useTenantNavigation();

  const [lookups, setLookups] = useState({});
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const today = format(new Date(), "yyyy-MM-dd");
  const monthStart = format(startOfMonth(new Date()), "yyyy-MM-dd");
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(today);
  const [siteFilter, setSiteFilter] = useState("all");
  const [contractorFilter, setContractorFilter] = useState("all");
  const [shiftFilter, setShiftFilter] = useState("all");
  const [deleteDialog, setDeleteDialog] = useState(null);

  const isGlobalAdmin = user?.role === "admin" && !user?.tenant_id;

  useEffect(() => {
    api.get("/lookups").then(setLookups).catch(() => {});
  }, []);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "20" });
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (siteFilter !== "all") params.set("site_id", siteFilter);
      if (contractorFilter !== "all") params.set("contractor_id", contractorFilter);
      if (shiftFilter !== "all") params.set("shift", shiftFilter);
      const res = await api.get(`/viewer/reports?${params}`);
      setReports(res.reports || []);
      setTotal(res.total || 0);
      setPages(res.pages || 1);
    } catch (err) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [page, dateFrom, dateTo, siteFilter, contractorFilter, shiftFilter]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const getSiteName = (id) => (lookups.sites || []).find(s => s.id === id)?.name || "-";
  const getContractorName = (id) => (lookups.contractors || []).find(c => c.id === id)?.name || "-";

  const [exporting, setExporting] = useState(false);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("token");
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (siteFilter !== "all") params.set("site_id", siteFilter);
      if (contractorFilter !== "all") params.set("contractor_id", contractorFilter);
      if (shiftFilter !== "all") params.set("shift", shiftFilter);
      const res = await fetch(`${backendUrl}/api/export/csv?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AMP_MineOps_Export.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch (err) {
      toast.error(err.message || "Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadPdf = async (reportId, theme) => {
    try {
      const token = localStorage.getItem("token");
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/reports/${reportId}/pdf?theme=${theme}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${reportId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error("Failed to download PDF");
    }
  };

  const handleDeleteReport = async () => {
    if (!deleteDialog) return;
    try {
      await api.delete(`/admin/reports/${deleteDialog.id}`);
      toast.success("Report deleted");
      setDeleteDialog(null);
      loadReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete report");
    }
  };

  const handleReopenReport = async (reportId) => {
    try {
      await api.put(`/admin/reports/${reportId}/reopen`);
      toast.success("Report reopened for editing");
      loadReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reopen report");
    }
  };

  return (
    <div className="min-h-screen" data-testid="viewer-page">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
          <Badge variant="outline" className="text-[10px] border-primary/30 text-primary ml-1">{isGlobalAdmin ? "Global Admin" : user?.role === "tenant_admin" ? "Tenant Admin" : user?.role === "admin" ? "Admin" : "Viewer"}</Badge>
        </div>
        <div className="flex items-center gap-3">
          {["admin", "tenant_admin"].includes(user?.role) && (
            <Button variant="outline" size="sm" className="border-border" onClick={() => navigate("/admin")} data-testid="back-to-admin-button">
              Admin Panel
            </Button>
          )}
          <Button variant="outline" size="sm" className="border-border" onClick={() => navigate("/analytics")} data-testid="viewer-analytics-button">
            <BarChart3 className="h-4 w-4 mr-1" strokeWidth={1.5} /> Analytics
          </Button>
          <NotificationBell />
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="viewer-logout-button">
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-headline text-2xl font-bold tracking-tight flex items-center gap-2">
              <FileText className="h-6 w-6 text-primary" strokeWidth={1.5} />
              Submitted Reports
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {total} report{total !== 1 ? "s" : ""} found
            </p>
          </div>
          {user?.role === "admin" && (
            <Button
              variant="outline"
              size="sm"
              className="border-border"
              onClick={handleExportCsv}
              disabled={exporting || total === 0}
              data-testid="export-csv-button"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1" strokeWidth={1.5} />
              {exporting ? "Exporting..." : "Export CSV"}
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="border-border bg-card mb-6" data-testid="viewer-filters">
          <CardContent className="py-4 px-5">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} className="bg-background border-border h-9 text-sm font-mono w-[150px]" data-testid="viewer-date-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} className="bg-background border-border h-9 text-sm font-mono w-[150px]" data-testid="viewer-date-to" />
              </div>
              <div className="space-y-1 min-w-[160px]">
                <Label className="text-xs text-muted-foreground">Site</Label>
                <Select value={siteFilter} onValueChange={v => { setSiteFilter(v); setPage(1); }}>
                  <SelectTrigger data-testid="viewer-site-filter" className="bg-background border-border h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Sites</SelectItem>
                    {(lookups.sites || []).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 min-w-[160px]">
                <Label className="text-xs text-muted-foreground">Contractor</Label>
                <Select value={contractorFilter} onValueChange={v => { setContractorFilter(v); setPage(1); }}>
                  <SelectTrigger data-testid="viewer-contractor-filter" className="bg-background border-border h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Contractors</SelectItem>
                    {(lookups.contractors || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 min-w-[120px]">
                <Label className="text-xs text-muted-foreground">Shift</Label>
                <Select value={shiftFilter} onValueChange={v => { setShiftFilter(v); setPage(1); }}>
                  <SelectTrigger data-testid="viewer-shift-filter" className="bg-background border-border h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Shifts</SelectItem>
                    <SelectItem value="Day">Day</SelectItem>
                    <SelectItem value="Night">Night</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reports Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">Loading reports...</div>
        ) : reports.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-16 text-center">
              <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" strokeWidth={1} />
              <p className="text-muted-foreground text-sm">No submitted reports found for the selected filters.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="border-border bg-card overflow-hidden mb-4">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground text-xs uppercase"><Calendar className="h-3 w-3 inline mr-1" />Date</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase"><MapPin className="h-3 w-3 inline mr-1" />Site</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase"><Clock className="h-3 w-3 inline mr-1" />Shift</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase"><Truck className="h-3 w-3 inline mr-1" />Contractor</TableHead>
                    <TableHead className="text-muted-foreground text-xs uppercase">Operator</TableHead>
                    <TableHead className="text-right text-muted-foreground text-xs uppercase">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((r) => (
                    <TableRow key={r.id} className="border-border" data-testid={`viewer-report-row-${r.id}`}>
                      <TableCell className="font-mono text-sm">{r.report_date}</TableCell>
                      <TableCell className="text-sm">{getSiteName(r.site_id)}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs border-border">{r.shift}</Badge></TableCell>
                      <TableCell className="text-sm">{getContractorName(r.contractor_id)}</TableCell>
                      <TableCell className="text-sm">{r.foreman || "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/report/${r.id}/review`)} data-testid={`view-report-${r.id}`} className="text-xs">
                            <Eye className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> View
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(r.id, "light")} data-testid={`pdf-light-${r.id}`} className="text-xs">
                            <Download className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> PDF
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(r.id, "dark")} data-testid={`pdf-dark-${r.id}`} className="text-xs text-primary">
                            <Download className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Dark
                          </Button>
                          {isGlobalAdmin && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleReopenReport(r.id)} data-testid={`reopen-report-${r.id}`} className="text-xs text-orange-400" title="Reopen for editing">
                                <RotateCcw className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Edit
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setDeleteDialog(r)} data-testid={`delete-report-${r.id}`} className="text-xs text-destructive" title="Delete report">
                                <Trash2 className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-between" data-testid="viewer-pagination">
                <p className="text-xs text-muted-foreground">Page {page} of {pages} ({total} reports)</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-border">
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> Prev
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="border-border">
                    Next <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={(open) => !open && setDeleteDialog(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-headline text-destructive">Delete Report</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              This action cannot be undone. All data associated with this report (equipment hours, material movements, delays, safety records) will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          {deleteDialog && (
            <div className="py-2 text-sm">
              <p><strong>Date:</strong> {deleteDialog.report_date}</p>
              <p><strong>Site:</strong> {getSiteName(deleteDialog.site_id)}</p>
              <p><strong>Shift:</strong> {deleteDialog.shift}</p>
              <p><strong>Contractor:</strong> {getContractorName(deleteDialog.contractor_id)}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="border-border">Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteReport} data-testid="confirm-delete-report">
              <Trash2 className="h-4 w-4 mr-1" strokeWidth={1.5} /> Delete Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
